/**
 * Theme token schema (catalog v0).
 *
 * Canonical contract: one token id → theme[id] (JS) and var(--{id}) (CSS).
 * See docs/theme-tokens-v1-spec.md.
 */

export const THEME_TOKEN_GROUPS = Object.freeze({
  SURFACES: 'surfaces',
  TEXT: 'text',
  BUTTONS: 'buttons',
  ACCENT: 'accent',
  STATUS: 'status',
  ORBIT: 'orbit',
});

/**
 * Editable color tokens. Ids match today's theme object keys (Phase 1).
 *
 * @type {ReadonlyArray<{
 *   id: string,
 *   title: string,
 *   description: string,
 *   group: string,
 *   type: 'color',
 *   editable: true,
 *   preview: string,
 * }>}
 */
export const THEME_COLOR_TOKENS = Object.freeze([
  {
    id: 'background',
    title: 'App background',
    description: 'Main app chrome behind sidebars, pages, and empty regions. The base canvas color.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'App shell / page canvas',
  },
  {
    id: 'backgroundMobile',
    title: 'Mobile background',
    description: 'Background used on compact/mobile shells when it must differ from desktop.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Mobile shell preview',
  },
  {
    id: 'backgroundFaded',
    title: 'Faded surface',
    description: 'Very subtle fill for quiet nested areas (barely visible wash).',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Nested quiet region',
  },
  {
    id: 'backgroundMuted',
    title: 'Muted surface',
    description: 'Light structural fill / divider-adjacent wash (rows, separators, soft blocks).',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'List row idle, soft block',
  },
  {
    id: 'backgroundHover',
    title: 'Hover surface',
    description: 'Fill when a clickable row/chip/control is hovered.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Sidebar row / list row hover',
  },
  {
    id: 'backgroundSelected',
    title: 'Selected surface',
    description: 'Fill for selected or active items (nav, lists, toggles).',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Selected nav/list item',
  },
  {
    id: 'panel',
    title: 'Panel',
    description: 'Elevated or inset panel fill for cards, chips, side regions, and similar surface-on-canvas blocks. Not the frosted glass wash derived from background.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Card, channel attach strip, chip',
  },
  {
    id: 'popup',
    title: 'Popup',
    description: 'Floating menu / popup panel background (menus, switcher popups).',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Context menu / popup',
  },
  {
    id: 'overlay',
    title: 'Overlay scrim',
    description: 'Dimmed veil behind modal sheets and dialogs.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Modal with scrim',
  },
  {
    id: 'input',
    title: 'Input field',
    description: 'Text field and similar control backgrounds.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Text input',
  },
  {
    id: 'status',
    title: 'Status bar',
    description: 'Compact status/chrome strip backgrounds where used.',
    group: THEME_TOKEN_GROUPS.SURFACES,
    type: 'color',
    editable: true,
    preview: 'Status strip',
  },
  {
    id: 'foreground',
    title: 'Primary text',
    description: 'Default body and label text on app backgrounds.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Body text',
  },
  {
    id: 'foregroundFaded',
    title: 'Secondary text',
    description: 'Softer primary-adjacent text (supporting copy).',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Secondary label',
  },
  {
    id: 'foregroundMuted',
    title: 'Muted text',
    description: 'De-emphasized text (hints, timestamps, placeholders-adjacent).',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Meta / hint text',
  },
  {
    id: 'foregroundSelected',
    title: 'Selected text',
    description: 'Text color on selected surfaces.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Selected list item label',
  },
  {
    id: 'outline',
    title: 'Outline',
    description: 'Default border/outline for controls and panels.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Bordered card / input',
  },
  {
    id: 'outlineMuted',
    title: 'Muted outline',
    description: 'Softer border for quiet separators and low-emphasis frames.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Subtle divider',
  },
  {
    id: 'message',
    title: 'Message text',
    description: 'Primary readable text in message/comment-style content.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Channel message / comment',
  },
  {
    id: 'selectionBackground',
    title: 'Text selection background',
    description: 'Browser/editor text selection highlight background.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Selected text in editor',
  },
  {
    id: 'selectionForeground',
    title: 'Text selection text',
    description: 'Text color inside a selection highlight.',
    group: THEME_TOKEN_GROUPS.TEXT,
    type: 'color',
    editable: true,
    preview: 'Selected text in editor',
  },
  {
    id: 'buttonBackgroundColor',
    title: 'Button background',
    description: 'Default filled button background (primary/default chrome).',
    group: THEME_TOKEN_GROUPS.BUTTONS,
    type: 'color',
    editable: true,
    preview: 'Primary button',
  },
  {
    id: 'buttonLabelColor',
    title: 'Button label',
    description: 'Label text on default filled buttons.',
    group: THEME_TOKEN_GROUPS.BUTTONS,
    type: 'color',
    editable: true,
    preview: 'Primary button',
  },
  {
    id: 'buttonIconColor',
    title: 'Button icon',
    description: 'Icon tint on default filled buttons.',
    group: THEME_TOKEN_GROUPS.BUTTONS,
    type: 'color',
    editable: true,
    preview: 'Icon button',
  },
  {
    id: 'buttonHoverBackgroundColor',
    title: 'Button hover background',
    description: 'Background when a button (or ghost chrome) is hovered.',
    group: THEME_TOKEN_GROUPS.BUTTONS,
    type: 'color',
    editable: true,
    preview: 'Button hover',
  },
  {
    id: 'buttonHoverLabelColor',
    title: 'Button hover label',
    description: 'Label text on hovered buttons.',
    group: THEME_TOKEN_GROUPS.BUTTONS,
    type: 'color',
    editable: true,
    preview: 'Button hover',
  },
  {
    id: 'buttonHoverIconColor',
    title: 'Button hover icon',
    description: 'Icon tint on hovered buttons.',
    group: THEME_TOKEN_GROUPS.BUTTONS,
    type: 'color',
    editable: true,
    preview: 'Icon button hover',
  },
  {
    id: 'highlight',
    title: 'Accent',
    description: 'Primary brand/accent color (links, emphasis, key CTAs, focus vibes).',
    group: THEME_TOKEN_GROUPS.ACCENT,
    type: 'color',
    editable: true,
    preview: 'Link, accent chip, primary emphasis',
  },
  {
    id: 'highlightHover',
    title: 'Accent hover',
    description: 'Accent color when hovered.',
    group: THEME_TOKEN_GROUPS.ACCENT,
    type: 'color',
    editable: true,
    preview: 'Hovered link / accent control',
  },
  {
    id: 'highlightForeground',
    title: 'Accent foreground',
    description: 'Text/icon color that sits on top of accent fills.',
    group: THEME_TOKEN_GROUPS.ACCENT,
    type: 'color',
    editable: true,
    preview: 'Accent button / badge on accent',
  },
  {
    id: 'highlightGlow',
    title: 'Accent glow',
    description: 'Soft glow behind primary/accent buttons (and similar accent emphasis). Independent of Accent fill — update this when Accent changes if you want the shadow to match.',
    group: THEME_TOKEN_GROUPS.ACCENT,
    type: 'color',
    editable: true,
    preview: 'Glow ring / accent wash',
  },
  {
    id: 'glow',
    title: 'Glow',
    description: 'Page/background soft wash (hero/orbit canvas). Not the button shadow — that is Accent glow.',
    group: THEME_TOKEN_GROUPS.ACCENT,
    type: 'color',
    editable: true,
    preview: 'Soft glow preview',
  },
  {
    id: 'success',
    title: 'Success',
    description: 'Success state color (positive confirmation).',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Success banner / icon',
  },
  {
    id: 'error',
    title: 'Error',
    description: 'Error state color (validation, failures).',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Error banner / icon',
  },
  {
    id: 'warning',
    title: 'Warning',
    description: 'Warning state color.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Warning banner / icon',
  },
  {
    id: 'active',
    title: 'Active',
    description: 'Active / live indicator color (distinct from selection fill).',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Active/live indicator',
  },
  {
    id: 'red',
    title: 'Red',
    description: 'Named red in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'orange',
    title: 'Orange',
    description: 'Named orange in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'yellow',
    title: 'Yellow',
    description: 'Named yellow in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'green',
    title: 'Green',
    description: 'Named green in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'teal',
    title: 'Teal',
    description: 'Named teal in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'blue',
    title: 'Blue',
    description: 'Named blue in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'purple',
    title: 'Purple',
    description: 'Named purple in the shared palette. Selectable as a calendar event color, with each theme mode defining its own shade.',
    group: THEME_TOKEN_GROUPS.STATUS,
    type: 'color',
    editable: true,
    preview: 'Palette swatch',
  },
  {
    id: 'orbitRing',
    title: 'Orbit ring',
    description: 'Primary orbit ring stroke/wash.',
    group: THEME_TOKEN_GROUPS.ORBIT,
    type: 'color',
    editable: true,
    preview: 'Orbit background',
  },
  {
    id: 'orbitRingMuted',
    title: 'Orbit ring muted',
    description: 'Softer orbit ring.',
    group: THEME_TOKEN_GROUPS.ORBIT,
    type: 'color',
    editable: true,
    preview: 'Orbit background',
  },
  {
    id: 'orbitHighlightGlow',
    title: 'Orbit accent glow',
    description: 'Accent glow on orbit treatment.',
    group: THEME_TOKEN_GROUPS.ORBIT,
    type: 'color',
    editable: true,
    preview: 'Orbit background',
  },
  {
    id: 'orbitDotSecondary',
    title: 'Orbit secondary dot',
    description: 'Secondary orbit dot color.',
    group: THEME_TOKEN_GROUPS.ORBIT,
    type: 'color',
    editable: true,
    preview: 'Orbit background',
  },
  {
    id: 'orbitDotSecondaryGlow',
    title: 'Orbit secondary glow',
    description: 'Glow for secondary orbit dot.',
    group: THEME_TOKEN_GROUPS.ORBIT,
    type: 'color',
    editable: true,
    preview: 'Orbit background',
  },
]);

export const THEME_COLOR_TOKEN_IDS = Object.freeze(
  THEME_COLOR_TOKENS.map((token) => token.id),
);

/**
 * Named palette tokens offered to users as selectable colors (for example calendar
 * event colors on temporal schema fields). Order defines swatch order in pickers.
 *
 * Selections are stored as token ids, so each theme mode resolves its own hex.
 */
export const THEME_PALETTE_TOKEN_IDS = Object.freeze([
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'purple',
]);

/**
 * Palette tokens with their designer-facing titles, in swatch order.
 *
 * @type {ReadonlyArray<{ id: string, title: string }>}
 */
export const THEME_PALETTE_TOKENS = Object.freeze(
  THEME_PALETTE_TOKEN_IDS.map((id) => {
    const token = THEME_COLOR_TOKENS.find((entry) => entry.id === id);
    return Object.freeze({ id, title: token?.title || id });
  }),
);

/**
 * Whether a value is a selectable named palette token id.
 *
 * @param {unknown} tokenId - Candidate token id.
 *
 * @returns {boolean} True when the id is part of the named palette.
 */
export function isThemePaletteTokenId(tokenId) {
  return THEME_PALETTE_TOKEN_IDS.includes(String(tokenId || '').trim().toLowerCase());
}

/**
 * Pack/mode metadata fields that remain on the resolved theme object but are
 * not color tokens in the designer grid.
 */
export const THEME_META_FIELD_IDS = Object.freeze([
  'key',
  'colorOptions',
  'enableBackground',
]);

/**
 * Derived CSS outputs (recipes). Not editable designer tokens — computed from
 * other tokens (and constants). CSS name is `--{id}`.
 *
 * Decision (Phase 4): keep glass fills as recipes on `background`, not
 * first-class editable tokens, until Theme Designer needs independent control.
 *
 * @type {ReadonlyArray<{
 *   id: string,
 *   title: string,
 *   description: string,
 *   type: 'color' | 'shadow' | 'length' | 'gradient',
 *   editable: false,
 *   derivesFrom: string[],
 * }>}
 */
export const THEME_RECIPES = Object.freeze([
  {
    id: 'glass',
    title: 'Glass',
    description: 'Translucent frosted fill (modals, glass chrome, some inputs). Derived from background @ ~91% opacity — not the panel token.',
    type: 'color',
    editable: false,
    derivesFrom: ['background'],
  },
  {
    id: 'glassSoft',
    title: 'Glass soft',
    description: 'Softer translucent fill for glass chrome. Derived from background @ ~80% opacity.',
    type: 'color',
    editable: false,
    derivesFrom: ['background'],
  },
  {
    id: 'glassBlur',
    title: 'Glass blur',
    description: 'Backdrop blur radius used with glass surfaces.',
    type: 'length',
    editable: false,
    derivesFrom: [],
  },
  {
    id: 'elevationShadow',
    title: 'Elevation shadow',
    description: 'Large soft drop shadow for elevated sheets/modals. Derived from foreground.',
    type: 'shadow',
    editable: false,
    derivesFrom: ['foreground'],
  },
  {
    id: 'elevationShadowSoft',
    title: 'Elevation shadow color',
    description: 'Soft shadow color stop used in composed box-shadows. Derived from foreground.',
    type: 'color',
    editable: false,
    derivesFrom: ['foreground'],
  },
  {
    id: 'focusRing',
    title: 'Focus ring',
    description: 'Focus outline shadow for glass/input chrome. Derived from highlight.',
    type: 'shadow',
    editable: false,
    derivesFrom: ['highlight'],
  },
  {
    id: 'focusRingColor',
    title: 'Focus ring color',
    description: 'Focus wash color used in overlays and rings. Derived from highlight.',
    type: 'color',
    editable: false,
    derivesFrom: ['highlight'],
  },
  {
    id: 'heroGlow',
    title: 'Hero glow',
    description: 'Radial accent glow decoration. Derived from highlight.',
    type: 'gradient',
    editable: false,
    derivesFrom: ['highlight'],
  },
  {
    id: 'heroBackground',
    title: 'Hero background',
    description: 'Screen/hero radial wash blending highlight into background.',
    type: 'gradient',
    editable: false,
    derivesFrom: ['highlight', 'background'],
  },
]);

export const THEME_RECIPE_IDS = Object.freeze(
  THEME_RECIPES.map((recipe) => recipe.id),
);

/**
 * Build CSS custom property name for a canonical token or recipe id.
 *
 * @param {string} id
 * @returns {string}
 */
export function cssVarNameForToken(id) {
  return `--${id}`;
}
